import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Globe, Building2, Check } from 'lucide-react';
import { LoadingOverlay } from '../ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { useUser } from '../../contexts/UserContext';
import { useTeams } from '../../contexts/TeamsContext';
import { OnboardingStatusEnum } from 'api-client';
import { sleep } from '../../utils/sleep';
import { StepIndicator } from './components/StepIndicator';
import { TeamSetup } from './steps/TeamSetup';
import { AssetDiscovery } from './steps/AssetDiscovery';
import { ScanProgress } from './steps/ScanProgress';
import { ScanResults } from './steps/ScanResults';
import { OnboardingStep } from './types';

const steps: OnboardingStep[] = [
  {
    title: 'Team Setup',
    description: 'Let\'s get started by setting up your team',
    icon: Building2,
    component: TeamSetup
  },
  {
    title: 'Asset Discovery',
    description: 'Configure your asset discovery settings',
    icon: Globe,
    component: AssetDiscovery
  },
  {
    title: 'Initial Scan',
    description: 'Running initial security scan',
    icon: Shield,
    component: ScanProgress
  },
  {
    title: 'Results',
    description: 'Review scan results',
    icon: Check,
    component: ScanResults
  }
];

export const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { completeOnboarding, setTeamStatus } = useUser();
  const { currentTeam } = useTeams();
  const [currentStep, setCurrentStep] = useState(0);
  const { isLoading, withLoading } = useLoading();

  // If onboarding is already completed, show message and redirect
  if (currentTeam?.onboardingStatus === OnboardingStatusEnum.Completed) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
        <div className="w-full max-w-2xl text-center">
          <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
            <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-white mb-2">Onboarding Already Completed</h2>
            <p className="text-gray-400 mb-6">Your team has already completed the onboarding process.</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleNext = async () => {
    await withLoading(async () => {
      await sleep(1000);
      
      if (currentStep === 0) {
        // After team setup
        setTeamStatus(true);
      }
      
      if (currentStep === steps.length - 1) {
        // Complete onboarding and redirect to dashboard
        completeOnboarding();
        navigate('/', { replace: true });
        return;
      }
      
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    });
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-hidden">
          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-800">
            <StepIndicator steps={steps} currentStep={currentStep} />
          </div>

          {/* Step Content */}
          <div className="p-6 relative">
            {isLoading && <LoadingOverlay />}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <CurrentStepComponent
                  onNext={handleNext}
                  onBack={handleBack}
                  isLoading={isLoading}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};